import { type ClassValue, clsx } from 'clsx'
import { customAlphabet } from 'nanoid'
import { twMerge } from 'tailwind-merge'
import {
  IconFacebook,
  IconInstagram,
  IconLinkedin,
  IconTwitter
} from '@/components/ui/icons'
import {
  differenceInMinutes,
  differenceInHours,
  differenceInDays,
  differenceInWeeks,
  differenceInMonths,
  format,
  isToday,
  isYesterday,
  isThisWeek,
  formatDistanceToNowStrict,
  parseISO,
  differenceInYears
} from 'date-fns'
import { Chat, GeniiusUser, Message, AuthorBio, getClerkUserInfoToMessageUserInfo } from '@/lib/types'
import { getPlanFromSubscription, pricingPlans } from '@/lib/plan-manager/pricing-plans'
import useChatListStore from '@/lib/hooks/zustand-stores/use-chat-list-store'
import { useClerk } from '@clerk/nextjs'
import { ObjectId } from 'mongodb'
import { saveChatInExternalDatabase, updateChatTitleInDB } from '@/app/actions/chats'
import { fetchUserIdByClerkUserId } from '@/app/actions/users'

export function formatRelativeTime(date: Date): string {
  return formatDistanceToNowStrict(date, { addSuffix: true })
}

export function formatChatDate(dateInput: Date | string | number, includeTimeAfter7Days: boolean = false): string {
  const date = dateInput instanceof Date ? dateInput : parseISO(dateInput as string);
  const now = new Date();
  const diffDays = differenceInDays(now, date);
  const diffYears = differenceInYears(now, date);

  if (isToday(date)) {
    return format(date, 'h:mm a')
  } else if (isYesterday(date)) {
    return `Yesterday ${format(date, 'h:mma').toLowerCase()}`
  } else if (diffDays <= 6) {
    return `${format(date, 'EEE')} ${format(date, 'h:mma').toLowerCase()}`
  } else if (diffYears === 0) {
    return includeTimeAfter7Days
      ? format(date, 'EEE, MMM d h:mma')
      : format(date, 'EEE, MMM d')
  } else {
    return includeTimeAfter7Days
      ? format(date, "EEE, MMM d ''yy h:mma")
      : format(date, "EEE, MMM d ''yy")
  }
}
export function formatWhatsAppStyle(date: Date | string | number): string {
  const dateObj = date instanceof Date ? date : new Date(date);

  if (isToday(dateObj)) {
    return format(dateObj, 'h:mm a').toLowerCase() // e.g., "2:34 pm"
  } else if (isYesterday(dateObj)) {
    return 'Yesterday'
  } else if (isThisWeek(dateObj)) {
    return format(dateObj, 'EEEE') // e.g., "Monday"
  }

  // For dates in this year
  if (dateObj.getFullYear() === new Date().getFullYear()) {
    return format(dateObj, 'MMM d') // e.g., "Sep 23"
  }

  // For older dates
  return format(dateObj, 'MM/dd/yy') // e.g., "09/23/22"
}

export function FormatDateAndTime(date: Date): string {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  };

  return date.toLocaleString('en-US', options);
}

export function formatMessageTime(date: Date): string {
  const now = new Date()
  const diffMinutes = differenceInMinutes(now, date)
  const diffHours = differenceInHours(now, date)
  const diffDays = differenceInDays(now, date)
  const diffWeeks = differenceInWeeks(now, date)
  const diffMonths = differenceInMonths(now, date)

  if (diffMinutes < 1) {
    return 'Just now'
  } else if (diffMinutes < 60) {
    return `${diffMinutes}m`
  } else if (diffHours < 24) {
    return `${diffHours}h`
  } else if (diffDays < 7) {
    return `${diffDays}d`
  } else if (diffWeeks < 4) {
    return `${diffWeeks}w`
  } else {
    return `${diffMonths}mo`
  }
}

// new-chatbot-lib/utils/formatContent.ts

/**
 * Formats content by highlighting usernames and converting plain text URLs into clickable links.
 * Handles cases where content might already be partially formatted.
 * @param content - The content string to be formatted.
 * @returns The formatted content with usernames and links highlighted.
 */
export function formatContent(content: string | any): string {
  // Check if content is not a string or is undefined/null
  if (content === null || content === undefined) {
    return '';
  }

  // Convert to string if it's not already a string
  if (typeof content !== 'string') {
    console.warn('formatContent received non-string content:', content);
    try {
      // For object types, try JSON.stringify
      if (typeof content === 'object') {
        content = JSON.stringify(content);
      } else {
        // For other types like numbers, boolean, etc.
        content = String(content);
      }
    } catch (e) {
      console.error('Failed to convert content to string:', e);
      return '';
    }
  }

  // Check for debug info and other JSON artifacts that should be formatted nicely
  const debugPatterns = [
    // Multi-step workflow, artifact, and evaluation JSON
    /\{[\s\S]*?"(isMultiStepWorkflow|artifact|evaluation)"[\s\S]+?\}/g,
    // Other potential artifact patterns
    /\{[\s\S]*?"(kind|content|title)"[\s\S]+?\}/g,
    // Debug info pattern
    /\{[\s\S]*?"debugInfo"[\s\S]+?\}/g
  ];

  // Replace raw JSON with interactive components
  for (const pattern of debugPatterns) {
    // Check if the content has JSON that matches our patterns
    if (pattern.test(content)) {
      content = content.replace(pattern, (match: string) => {
        try {
          // Try to parse to verify it's actually JSON
          const jsonObj = JSON.parse(match);

          // If it's debug info or has debug-specific keys, format it as a collapsible component
          const isDebugInfo =
            'debugInfo' in jsonObj ||
            'isMultiStepWorkflow' in jsonObj ||
            'evaluation' in jsonObj ||
            'artifact' in jsonObj;

          if (isDebugInfo) {
            // Determine what type of debug info this is to show a more specific label
            let infoType = "Debug Information";
            if ('isMultiStepWorkflow' in jsonObj) infoType = "Workflow Information";
            if ('evaluation' in jsonObj) infoType = "Evaluation Results";
            if ('artifact' in jsonObj) infoType = "Artifact Details";
            if ('debugInfo' in jsonObj) infoType = "Debug Information";

            // Return a specially formatted string that will be processed by the Markdown component
            const prettyJson = JSON.stringify(jsonObj, null, 2);
            return `
<details>
  <summary>**${infoType}** (Click to expand)</summary>
  <div class="debug-info-content">
    
\`\`\`json
${prettyJson}
\`\`\`

  </div>
</details>`;
          }

          // For other types of JSON we don't recognize, provide a simpler message
          return '\n\n> **Note**: Additional technical information is available but is not displayed in the chat interface.';
        } catch (e) {
          // If it's not valid JSON, keep the original content
          return match;
        }
      });
    }
  }

  // Now that we have ensured content is a string, we can proceed with formatting
  // First, let's check if this content is already formatted
  const isPreFormattedLink = (text: string) => {
    return /^\[.+\]\(.+\)$/.test(text)
  }

  const isPreFormattedMention = (text: string) => {
    return /^\[@.+\]\/profile\/.+\)$/.test(text)
  }

  const isEmailAddress = (text: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text)
  }

  // First pass: fix incorrectly formatted email addresses in the entire content
  content = content.replace(/(\S+)\[@([^\]]+)\]\(\/profile\/\2\)/g, '$1@$2')

  // Split content into lines to handle each line separately
  const lines = content.split('\n')
  const formattedLines = lines.map((line: string) => {
    // Split by spaces to handle each word
    const words = line.split(' ')
    const formattedWords = words.map((word: string) => {
      // Skip if word is already a formatted link or mention
      if (isPreFormattedLink(word) || isPreFormattedMention(word)) {
        return word
      }

      // Handle email addresses
      if (isEmailAddress(word)) {
        return word
      }

      // Handle mentions with any trailing punctuation or special characters
      const mentionMatch = word.match(/^@([a-zA-Z0-9_-]+)(.*?)$/)
      if (mentionMatch) {
        const [, username, trailing] = mentionMatch
        // Only format if the username part is valid
        if (username) {
          return `[@${username}](/profile/${username})${trailing}`
        }
      }

      // Handle URLs
      if (word.match(/^https?:\/\/[^\s]+$/)) {
        return `[${word}](${word})`
      }

      return word
    })

    return formattedWords.join(' ')
  })

  return formattedLines.join('\n')
}

export function debug(msg: any, ...objects: any[]) {
  if (process.env.NODE_ENV === 'development') {
    console.log(msg, ...objects)
  }
}

export const truncateText = (input: string | string[], maxLength: number) => {
  const text = Array.isArray(input) ? input.join(', ') : input
  if (text.length > maxLength) {
    return text.slice(0, maxLength) + '...'
  }
  return text
}



export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const nanoid = customAlphabet(
  '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz',
  7
) // 7-character random string

export async function fetcher<JSON = any>(
  input: RequestInfo,
  init?: RequestInit
): Promise<JSON> {
  const res = await fetch(input, init)

  if (!res.ok) {
    const json = await res.json()
    if (json.error) {
      const error = new Error(json.error) as Error & {
        status: number
      }
      error.status = res.status
      throw error
    } else {
      throw new Error('An unexpected error occurred')
    }
  }

  return res.json()
}

export function formatDate(input: string | number | Date): string {
  const date = new Date(input)
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  })
}

export const formatNumber = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(value)

export const runAsyncFnWithoutBlocking = (
  fn: (...args: any) => Promise<any>
) => {
  fn()
}

export const sleep = (ms: number) =>
  new Promise(resolve => setTimeout(resolve, ms))

export const getStringFromBuffer = (buffer: ArrayBuffer) =>
  Array.from(new Uint8Array(buffer))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')

export enum ResultCode {
  InvalidCredentials = 'INVALID_CREDENTIALS',
  InvalidSubmission = 'INVALID_SUBMISSION',
  UserAlreadyExists = 'USER_ALREADY_EXISTS',
  UnknownError = 'UNKNOWN_ERROR',
  UserCreated = 'USER_CREATED',
  UserLoggedIn = 'USER_LOGGED_IN'
}

export const getMessageFromCode = (resultCode: string) => {
  switch (resultCode) {
    case ResultCode.InvalidCredentials:
      return 'Invalid credentials!'
    case ResultCode.InvalidSubmission:
      return 'Invalid submission, please try again!'
    case ResultCode.UserAlreadyExists:
      return 'User already exists, please log in!'
    case ResultCode.UserCreated:
      return 'User created, welcome!'
    case ResultCode.UnknownError:
      return 'Something went wrong, please try again!'
    case ResultCode.UserLoggedIn:
      return 'Logged in!'
  }
}

export const getCurrentUserObjId = async (user: any) => {
  try {
    if (user) {
      const _userId = user?.unsafeMetadata?._id
      if (_userId) return _userId
      return await fetchUserIdByClerkUserId(user.id)
    }
  } catch (e: any) {
    debug('Failed to get user Id', e)
  }
}

export const domainIconMap = {
  x: IconTwitter,
  facebook: IconFacebook,
  linkedin: IconLinkedin,
  instagram: IconInstagram
}

export const getDomainFromUrl = (url: string) => {
  try {
    const parsedUrl = new URL(url)
    let { hostname } = parsedUrl
    hostname = hostname.replace(/^www\./, '') // Remove 'www.' if present
    const parts = hostname.split('.')
    return parts.length > 2 ? parts.slice(1, -1).join('.') : parts[0]
  } catch (error) {
    console.error('Error extracting domain:', error)
    return undefined
  }
}

export function toBoolean(value: string | undefined): boolean {
  return (
    value?.toLowerCase() === 'true' ||
    value === '1' ||
    value?.toLowerCase() === 'yes'
  )
}

export const NEW_CHAT_TITLE = 'new chat'

export const createPlainChat = (
  id: string,
  userId: string,
  userImageUrl: string,
  userFirstName: string,
  userLastName: string,
  provider: string,
  model: string,
  chatTitle?: string,
) => ({
  _id: id,
  title: chatTitle || NEW_CHAT_TITLE,
  createdAt: new Date(),
  userId: userId,
  imageUrl: userImageUrl,
  path: `/chat/${id}`,
  isPublic: false,
  permittedUsers: [
    {
      _id: userId,
      memberType: 'admin'
    }
  ],
  userInfo: {
    _id: userId,
    firstName: userFirstName,
    lastName: userLastName,
    imageUrl: userImageUrl,
    username: 'username' //TODO: add username
  },
  provider,
  model
})

export const createNewChatInDB = async (
  id: string,
  userId: string,
  userImageUrl: string,
  userFirstName: string,
  userLastName: string,
  provider: string,
  model: string,
  chatTitle?: string
) => {
  if (userId) {
    const plainChat = createPlainChat(
      id,
      userId,
      userImageUrl,
      userFirstName,
      userLastName,
      provider,
      model,
      chatTitle,
    )
    const res = await saveChatInExternalDatabase(plainChat as any)
    return res?.chat
  }
}

export const checkForIdInNotifications = (
  targetId: string,
  notifications: any
) => {
  return notifications.some((notification: { item: { id: string }[] }) =>
    notification.item.some((item: { id: string }) => item.id === targetId)
  )
}

export const countOccurrencesOfIdInNotifications = (
  targetId: string,
  notifications: any
): number => {
  if (!notifications) return 0
  return notifications.reduce(
    (count: number, notification: { item: { id: string }[] }) => {
      return (
        count +
        notification.item.filter((item: { id: string }) => item.id === targetId)
          .length
      )
    },
    0
  )
}

export const notificationsListById = (
  notifications: any[],
  targetId: string
): string[] => {
  return notifications
    .filter(
      notif =>
        notif.item &&
        Array.isArray(notif.item) &&
        notif.item.some((item: { id: string }) => item.id === targetId)
    )
    .map(notif => notif._id)
}

export const filterNotificationsById = (
  notifications: any[],
  targetId: string
): any[] => {
  return notifications.filter(
    notif =>
      notif.item &&
      Array.isArray(notif.item) &&
      notif.item.some((item: { id: string }) => item.id === targetId)
  )
}

export const changeChatTitle = async ({
  chatId,
  isPublic,
  input,
  sendSystemMessage, updateChatTitle,
  refreshChatInList,
  user,
  currentChatTitle
}: {
  chatId: string
  input: string
  sendSystemMessage: (message: string) => void
  updateChatTitle: any
  refreshChatInList: any
  isPublic: boolean
  currentChatTitle: string
  user?: GeniiusUser | null
}) => {
  if (input !== currentChatTitle) {
    const res = await updateChatTitleInDB(chatId, input, isPublic || false)
    if (res.message) {
      const systemMessage = user
        ? `${user?.fullName || user?.firstName || user?.username} changed the chat title from "${currentChatTitle}" to "${input}"`
        : `Title was changed from "${currentChatTitle}" to "${input}`
      sendSystemMessage(systemMessage)
      updateChatTitle(chatId, input)
      refreshChatInList(chatId.toString())
    }
  }
}

export function getPermittedUsersFirstnames(permittedUsers: any) {
  if (!Array.isArray(permittedUsers) || permittedUsers.length === 0) {
    return ''
  }

  const firstnames = permittedUsers
    .map(user => user.firstName)
    .filter(firstName => firstName && typeof firstName === 'string')

  return firstnames.join(', ')
}


export function getPermittedUsersIds(permittedUsers: any) {
  if (!Array.isArray(permittedUsers) || permittedUsers.length === 0) {
    return []
  }
  return  permittedUsers.map(user => user._id)
}

export const parseSummary = (summary: string | null): Partial<AuthorBio> => {
  if (!summary) return {};

  const bioData: Partial<AuthorBio> = {};
  const sections = summary.split('\n\n');

  sections.forEach(section => {
    const [sectionTitle, ...content] = section.split('\n');
    switch (sectionTitle.trim()) {
      case 'About Me:':
        bioData.aboutMe = content.join('\n').trim();
        break;
      case 'Headline:':
        bioData.headline = content.join('\n').trim();
        break;
      case 'Location:':
        const location: Partial<AuthorBio['location']> = {};
        content.forEach(line => {
          const [key, value] = line.split(':').map(item => item.trim());
          switch (key) {
            case 'City':
              location.city = value;
              break;
            case 'State':
              location.state = value;
              break;
            case 'Country':
              location.country = value;
              break;
          }
        });
        if (Object.keys(location).length > 0) {
          bioData.location = location;
        }
        break;
      case 'Work Experience:':
        bioData.employer = content[0].replace('- ', '').split(' at ')[1]?.trim();
        break;
      case 'Education:':
        bioData.school = content[0].replace('- ', '').trim();
        break;
      case 'Interests:':
        bioData.interests = content.map(interest => interest.replace('- ', '').trim()).filter(Boolean);
        break;
      case 'Places Known Well:':
        bioData.placesKnownWell = content.map(place => place.replace('- ', '').trim().replace(', ', ' - ')).filter(Boolean);
        break;
    }
  });

  // Trim all string values in bioData
  (Object.keys(bioData) as Array<keyof AuthorBio>).forEach(key => {
    if (typeof bioData[key] === 'string') {
      bioData[key] = bioData[key]!.trim() as any;
    } else if (key === 'location' && bioData.location) {
      Object.keys(bioData.location).forEach(subKey => {
        if (typeof bioData.location![subKey as keyof typeof bioData.location] === 'string') {
          bioData.location![subKey as keyof typeof bioData.location] =
            bioData.location![subKey as keyof typeof bioData.location]!.trim();
        }
      });
    }
  });

  return bioData;
};

export function isProfileUrl(url: string, platform: string): boolean {
  switch (platform.toLowerCase()) {
    case 'linkedin':
      return /^https:\/\/(?:www\.|[a-z]{2}\.)?linkedin\.com\/in\/[a-zA-Z0-9-]+(?:\/.*|\?.*)?$/.test(url);
    case 'twitter':
      return /^https:\/\/(?:www\.)?(?:twitter\.com|x\.com)\/[^/]+\/?$/.test(url);
    case 'facebook':
      return /^https:\/\/(www|m)\.facebook\.com\/(?:[^/]+\/?|people\/[^/]+\/\d+\/?$)/.test(url);
    case 'instagram':
      return /^https:\/\/www\.instagram\.com\/[^/]+(?:\/?$|\/\?)/.test(url);
    case 'pinterest':
      return /^https:\/\/(?:www\.|[a-z]{2}\.)pinterest\.com\/[^/]+\/?$/.test(url);
    case 'tiktok':
      return /^https:\/\/www\.tiktok\.com\/@[^/]+\/?$/.test(url);
    default:
      return false;
  }
}

export const capitalizeTitle = (title: string): string => {
  if (title.length === 0) return title;
  return title[0].toUpperCase() + title.slice(1);
}


export const formatAnonymousDescription = (
  location?: {
    country?: string,
    state?: string,
    city?: string
  },
  subscriptionDetails?: {
    subscription_id?: string;
    product_id?: string;
    is_active?: boolean;
  }
): string => {
  const { category, name } = getPlanFromSubscription(subscriptionDetails);
  const isPremium = name === 'premium' || category === 'business';
  const userType = isPremium ? 'A premium mixus user' : 'A mixus user';

  if (location) {
    if (location.country && location.state && location.city) {
      return `${userType} from ${location.city}, ${location.state}, ${location.country}`;
    }
    if (location.state && location.country) {
      return `${userType} from ${location.state}, ${location.country}`;
    }
    if (location.country) {
      return `${userType} from ${location.country}`;
    }
  }
  return userType;
};

export const freeMessages = pricingPlans
  .find(cat => cat.name === 'personal')
  ?.plans.free.aiMessagesPerDay || 20;

export const createSlug = (title: string): string => {
  return title
      ?.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-')         // Replace spaces with hyphens
      .replace(/-+/g, '-')          // Remove consecutive hyphens
      .trim()                       // Trim whitespace
    || 'untitled'                 // Fallback if title is empty/undefined
}


interface AiUsage {
  count: number;
  date: string;
}

export const getUpdatedAiUsage = (currentAiUsage: AiUsage | {} | null | undefined): AiUsage => {
  const today = new Date().toISOString().slice(0, 10);

  // Handle empty/null/undefined cases
  if (!currentAiUsage || Object.keys(currentAiUsage).length === 0) {
    return {
      count: 1,
      date: today
    };
  }

  const usage = currentAiUsage as AiUsage;

  // If it's a new day or date is missing, create a fresh usage object
  if (!usage.date || usage.date !== today) {
    return {
      count: 1,
      date: today
    };
  }

  // Same day - increment count
  return {
    date: today,
    count: (usage.count || 0) + 1
  };
}


export const cleanLocalStorage = () => {
    localStorage.clear();
    sessionStorage.clear();

    const allKeys = Object.keys(localStorage);
    allKeys.forEach(key => {
      localStorage.removeItem(key);
    });
    const sessionKeys = Object.keys(sessionStorage);
    sessionKeys.forEach(key => {
      sessionStorage.removeItem(key);
    });
    useChatListStore.getState().clearStore();
}

export function cosineSimilarity(a: number[], b: number[]): number {
  const dotProduct = a.reduce((sum, a_i, idx) => sum + a_i * b[idx], 0);
  const normA = Math.sqrt(a.reduce((sum, a_i) => sum + a_i * a_i, 0));
  const normB = Math.sqrt(b.reduce((sum, b_i) => sum + b_i * b_i, 0));
  return dotProduct / (normA * normB);
}


export async function createChatObject(
  user: any,
  chatId: string,
  modelSelected: string,
  providerSelected: string
) {
  try {
    const createdAt = new Date()
    const path = `/chat/${chatId.toString()}`
    const title = 'new chat'
    const isPublic = false
    const publicPath = ''
    const imageUrl = user.imageUrl
    const userInfo = getClerkUserInfoToMessageUserInfo(user)
    const model = modelSelected
    const provider = providerSelected
    userInfo._id = user._id

    return {
      _id: chatId,
      createdAt,
      userId: userInfo._id,
      permittedUsers: [{ _id: userInfo._id, memberType: 'admin' }],
      title,
      isPublic,
      path,
      publicPath,
      imageUrl,
      userInfo,
      model,
      provider
    }
  } catch (error) {
    console.error('Error creating chat object:', error)
    return null
  }
}


export function isValidChat(chat: any): chat is Chat {
  return (
    chat !== null &&
    typeof chat === 'object' &&
    typeof chat._id === 'string' &&
    typeof chat.title === 'string' &&
    typeof chat.isPublic === 'boolean' &&
    chat.createdAt instanceof Date &&
    (chat.userInfo === undefined ||
     chat.userInfo === null ||
     (typeof chat.userInfo === 'object' &&
      (chat.userInfo.fullName === undefined || typeof chat.userInfo.fullName === 'string') &&
      (chat.userInfo.username === undefined || typeof chat.userInfo.username === 'string')))
  )
}

export function createPromiseWithResolvers<T>() {
  let resolve: (value: T | PromiseLike<T>) => void;
  let reject: (reason?: any) => void;
  const promise = new Promise<T>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { promise, resolve: resolve!, reject: reject! };
}


export function OrganizationAssetName(fullOrgId: string, addSuffix = true): string | null {
  const env = process.env.NEXT_PUBLIC_VERCEL_ENV;
  const suffix = env === 'production' ? '' : '-dev';
  const match = fullOrgId.match(/^org_(.+)$/);
  if (!match) {
    return null;
  }
  return `org-${match[1].toLowerCase()}${addSuffix ? suffix: ''}`;
}
